/**
 * NotificationPreferences - Settings component for notification preferences
 * Allows medics to customize which notifications they receive
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { 
  Bell, 
  BellRing, 
  BellOff,
  Smartphone,
  Mail,
  Loader2,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { pushNotificationsAPI } from '../../lib/api';
import { usePushNotifications } from '../../hooks/usePushNotifications';

export default function NotificationPreferences() {
  const {
    isSupported,
    isSubscribed,
    permissionState,
    loading: pushLoading,
    error: pushError,
    subscribe,
    unsubscribe
  } = usePushNotifications();

  const [preferences, setPreferences] = useState({
    push_enabled: true,
    push_shift_assigned: true,
    push_shift_changes: true,
    push_shift_reminder: true,
    push_compliance: true,
    push_expenses: true,
    in_app_enabled: true,
    email_enabled: true,
    email_shift_reminder: true,
    email_compliance: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch preferences
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await pushNotificationsAPI.getPreferences();
        setPreferences(response.data);
      } catch (error) {
        console.error('Failed to fetch preferences:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPreferences();
  }, []);

  // Update a single preference
  const updatePreference = async (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    
    try {
      setSaving(true);
      await pushNotificationsAPI.updatePreferences({ [key]: value });
      toast.success('Preferences saved');
    } catch (error) {
      toast.error('Failed to save preference');
      // Revert on error
      setPreferences(prev => ({ ...prev, [key]: !value }));
    } finally {
      setSaving(false);
    }
  };

  // Handle push subscription toggle
  const handlePushToggle = async (enabled) => {
    if (enabled) {
      const success = await subscribe();
      if (success) {
        toast.success('Push notifications enabled');
        updatePreference('push_enabled', true);
      }
    } else {
      const success = await unsubscribe();
      if (success) {
        toast.success('Push notifications disabled');
        updatePreference('push_enabled', false);
      }
    }
  };

  // Send test notification
  const sendTestNotification = async () => {
    try {
      await pushNotificationsAPI.sendTest();
      toast.success('Test notification sent! Check your device.');
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  };

  if (loading) {
    return (
      <Card className="bg-[#2B2B2B] border-white/5">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-[#1ABC9C]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#2B2B2B] border-white/5" data-testid="notification-preferences">
      <CardHeader>
        <CardTitle className="text-[#F2F2F2] font-['Manrope'] flex items-center gap-2">
          <Bell className="w-5 h-5 text-[#1ABC9C]" />
          Notification Preferences
        </CardTitle>
        <CardDescription className="text-[#A1A1AA]">
          Choose how you want to be notified about shifts, compliance, and expenses
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Browser Push Notifications Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-[#1ABC9C]" />
            <h3 className="font-medium text-[#F2F2F2]">Browser Push Notifications</h3>
          </div>
          
          {!isSupported ? (
            <Alert className="bg-[#FFC04D]/10 border-[#FFC04D]/20">
              <AlertTriangle className="h-4 w-4 text-[#FFC04D]" />
              <AlertDescription className="text-[#FFC04D]">
                Push notifications are not supported in this browser.
              </AlertDescription>
            </Alert>
          ) : permissionState === 'denied' ? (
            <Alert className="bg-red-500/10 border-red-500/20">
              <BellOff className="h-4 w-4 text-red-400" />
              <AlertDescription className="text-red-400">
                Push notifications are blocked. Please enable them in your browser settings.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-lg">
                <div className="flex items-center gap-3">
                  {isSubscribed ? (
                    <BellRing className="w-5 h-5 text-[#1ABC9C]" />
                  ) : (
                    <BellOff className="w-5 h-5 text-[#A1A1AA]" />
                  )}
                  <div>
                    <p className="text-sm text-[#F2F2F2]">
                      {isSubscribed ? 'Push notifications enabled' : 'Enable push notifications'}
                    </p>
                    <p className="text-xs text-[#A1A1AA]">
                      Get notified even when the app is closed
                    </p>
                  </div>
                </div>
                <Switch
                  checked={isSubscribed}
                  onCheckedChange={handlePushToggle}
                  disabled={pushLoading}
                />
              </div>
              
              {isSubscribed && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={sendTestNotification}
                  className="border-white/10 text-[#A1A1AA] hover:text-[#F2F2F2]"
                >
                  Send Test Notification
                </Button>
              )}
              
              {pushError && (
                <p className="text-xs text-red-400">{pushError}</p>
              )}
            </>
          )}
          
          {/* Push notification type toggles */}
          {isSubscribed && (
            <div className="space-y-3 pl-4 border-l-2 border-white/10">
              <PreferenceToggle
                label="Shift assignments"
                description="When you're assigned a new shift"
                checked={preferences.push_shift_assigned}
                onChange={(v) => updatePreference('push_shift_assigned', v)}
              />
              <PreferenceToggle
                label="Shift changes"
                description="When shift is confirmed, updated, or cancelled"
                checked={preferences.push_shift_changes}
                onChange={(v) => updatePreference('push_shift_changes', v)}
              />
              <PreferenceToggle
                label="Shift reminders"
                description="24-hour reminder before your shift"
                checked={preferences.push_shift_reminder}
                onChange={(v) => updatePreference('push_shift_reminder', v)}
              />
              <PreferenceToggle
                label="Compliance alerts"
                description="Document expiry warnings"
                checked={preferences.push_compliance}
                onChange={(v) => updatePreference('push_compliance', v)}
              />
              <PreferenceToggle
                label="Expense updates"
                description="When expenses are approved or rejected"
                checked={preferences.push_expenses}
                onChange={(v) => updatePreference('push_expenses', v)}
              />
            </div>
          )}
        </div>

        <Separator className="bg-white/10" />

        {/* In-App Notifications Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-[#1ABC9C]" />
            <h3 className="font-medium text-[#F2F2F2]">In-App Notifications</h3>
          </div>
          
          <PreferenceToggle
            label="Show in-app notifications"
            description="Display notifications in the app's notification center"
            checked={preferences.in_app_enabled}
            onChange={(v) => updatePreference('in_app_enabled', v)}
          />
        </div>

        <Separator className="bg-white/10" />

        {/* Email Notifications Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#1ABC9C]" />
            <h3 className="font-medium text-[#F2F2F2]">Email Notifications</h3>
          </div>
          
          <PreferenceToggle
            label="Enable email notifications"
            description="Receive notifications via email"
            checked={preferences.email_enabled}
            onChange={(v) => updatePreference('email_enabled', v)}
          />
          
          {preferences.email_enabled && (
            <div className="space-y-3 pl-4 border-l-2 border-white/10">
              <PreferenceToggle
                label="Shift reminders"
                description="Email reminder before your shift"
                checked={preferences.email_shift_reminder}
                onChange={(v) => updatePreference('email_shift_reminder', v)}
              />
              <PreferenceToggle
                label="Compliance alerts"
                description="Email when documents are expiring"
                checked={preferences.email_compliance}
                onChange={(v) => updatePreference('email_compliance', v)}
              />
            </div>
          )}
        </div>

        {saving && (
          <div className="flex items-center gap-2 text-xs text-[#A1A1AA]">
            <Loader2 className="w-3 h-3 animate-spin" />
            Saving...
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Reusable preference toggle component
function PreferenceToggle({ label, description, checked, onChange }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <Label className="text-sm text-[#F2F2F2]">{label}</Label>
        <p className="text-xs text-[#A1A1AA]">{description}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
      />
    </div>
  );
}
